import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, message, DatePicker, Select, Row, Col } from 'antd';
import { SaveOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import useCompanyNavigate from '../../utils/useCompanyNavigate';
import { batchApi, jadeTypeApi, categoryApi, photoTypeApi } from '../../services/api';
import PhotoUpload from '../../components/PhotoUpload';
import dayjs from 'dayjs';

const { TextArea } = Input;

const BatchForm = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [batch, setBatch] = useState(null);
  const [jadeTypes, setJadeTypes] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [photoTypeMap, setPhotoTypeMap] = useState({});
  const [batchLevelTypeIds, setBatchLevelTypeIds] = useState([]); // 批次级照片类型ID列表
  const navigate = useCompanyNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  useEffect(() => {
    fetchJadeTypes();
    fetchCategories();
    fetchPhotoTypes();
    if (isEdit) {
      fetchBatch();
    }
  }, [id]);

  const fetchJadeTypes = async () => {
    try {
      const res = await jadeTypeApi.all();
      setJadeTypes(res.data.map(j => ({ label: j.name, value: j.id })));
    } catch (err) {
      console.error('获取料种列表失败:', err);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await categoryApi.all();
      setCategories(res.data || []);
      setCategoryOptions((res.data || []).map(c => ({ label: c.name, value: c.name })));
    } catch {}
  };

  const fetchPhotoTypes = async () => {
    try {
      const res = await photoTypeApi.all();
      const map = {};
      const batchIds = [];
      (res.data || []).forEach(pt => {
        map[pt.id] = pt.name;
        if (pt.level === 'batch' && pt.status !== 0) batchIds.push(pt.id);
      });
      setPhotoTypeMap(map);
      setBatchLevelTypeIds(batchIds);
    } catch {}
  };

  const fetchBatch = async () => {
    try {
      const res = await batchApi.detail(id);
      setBatch(res.data);
      form.setFieldsValue({
        batchNo: res.data.batch_no,
        name: res.data.name,
        jadeTypeId: res.data.jade_type_id,
        category: res.data.category,
        rawMaterialOrigin: res.data.raw_material_origin,
        status: res.data.status,
        rawMaterialDate: res.data.raw_material_date ? dayjs(res.data.raw_material_date) : null,
        rawMaterialEntryDate: res.data.raw_material_entry_date ? dayjs(res.data.raw_material_entry_date) : null,
        description: res.data.description,
      });
    } catch (err) {
      navigate('/admin/batches');
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const data = {
        ...values,
        rawMaterialDate: values.rawMaterialDate ? values.rawMaterialDate.format('YYYY-MM-DD') : null,
        rawMaterialEntryDate: values.rawMaterialEntryDate ? values.rawMaterialEntryDate.format('YYYY-MM-DD') : null,
      };

      if (isEdit) {
        await batchApi.update(id, data);
        message.success('更新成功');
      } else {
        const res = await batchApi.create(data);
        message.success('创建成功');
        navigate(`/admin/batches/${res.data.id}/edit`, { replace: true });
      }
    } catch (err) {
      console.error('提交失败:', err);
    } finally {
      setLoading(false);
    }
  };

  // 获取当前品类的批次级照片类型列表
  const currentCategory = form.getFieldValue('category') || batch?.category;
  const selectedCategory = categories.find(c => c.name === currentCategory);
  const categoryBatchPhotoTypes = selectedCategory
    ? selectedCategory.photo_types.split(',').map(Number).filter(n => !isNaN(n) && batchLevelTypeIds.includes(n))
    : [];

  // 渲染品类关联的照片上传卡片
  const renderCategoryPhotoCards = () => {
    if (!isEdit || categoryBatchPhotoTypes.length === 0) return null;

    return categoryBatchPhotoTypes.map(typeId => (
      <Card key={typeId} title={photoTypeMap[typeId] || `类型${typeId}`} style={{ marginBottom: 16 }}>
        <PhotoUpload
          batchId={id}
          isBatchUpload={true}
          photoType={typeId}
          rawMaterialPhotos={batch?.photos?.[String(typeId)] || []}
          onUploadSuccess={fetchBatch}
        />
      </Card>
    ));
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24, gap: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/batches')}>
          返回
        </Button>
        <h2 style={{ margin: 0 }}>{isEdit ? '编辑批次' : '新增批次'}</h2>
      </div>

      <Row gutter={24}>
        <Col xs={24} lg={16}>
          <Card>
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              autoComplete="off"
            >
              <Form.Item
                name="batchNo"
                label="批次编号"
                rules={[{ required: true, message: '请输入批次编号' }]}
              >
                <Input placeholder="如: HT2024001" disabled={isEdit} />
              </Form.Item>

              <Form.Item
                name="name"
                label="批次名称"
                rules={[{ required: true, message: '请输入批次名称' }]}
              >
                <Input placeholder="请输入批次名称" />
              </Form.Item>

              <Form.Item
                name="jadeTypeId"
                label="料种"
              >
                <Select
                  placeholder="请选择料种"
                  options={jadeTypes}
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    option.label.toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>

              <Form.Item
                name="category"
                label="品类"
              >
                <Select
                  placeholder="请选择品类"
                  options={categoryOptions}
                  allowClear
                />
              </Form.Item>

              <Form.Item
                name="rawMaterialOrigin"
                label="原石产地"
              >
                <Input placeholder="如: 新疆和田" />
              </Form.Item>

              {isEdit && (
                <Form.Item name="status" label="状态">
                  <Select
                    options={[
                      { label: '未开始', value: 0 },
                      { label: '生产中', value: 1 },
                      { label: '已完成', value: 2 },
                    ]}
                  />
                </Form.Item>
              )}

              <Form.Item name="rawMaterialDate" label="原石日期">
                <DatePicker style={{ width: '100%' }} placeholder="选择日期" />
              </Form.Item>

              <Form.Item name="rawMaterialEntryDate" label="入库日期">
                <DatePicker style={{ width: '100%' }} placeholder="选择入库日期" />
              </Form.Item>

              <Form.Item name="description" label="描述/备注">
                <TextArea rows={4} placeholder="请输入批次描述，如原石产地、规格等信息" />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={loading}
                  size="large"
                  style={{ background: '#8B7355' }}
                >
                  {isEdit ? '保存修改' : '创建批次'}
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        {isEdit && (
          <Col xs={24} lg={8}>
            {renderCategoryPhotoCards()}
          </Col>
        )}
      </Row>
    </div>
  );
};

export default BatchForm;
